import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { INDUSTRIES, JOB_TYPES, STATUS_STYLES, STATUS_STYLES_LIGHT, INPUT_LIGHT, SELECT_CONTENT_LIGHT } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const EMPTY_JOB = {
  title: "", company: "", location: "", industry: INDUSTRIES[0], type: "Full-time",
  experience: "", salary: "", skills: "", description: "", status: "open",
};

export const inputCls = "bg-white/5 border-white/10 text-slate-200 focus-visible:ring-brand";

export const StatusBadge = ({ status, light = false }) => (
  <Badge
    variant="outline"
    className={`${(light ? STATUS_STYLES_LIGHT : STATUS_STYLES)[status] || (light ? "border-slate-200 bg-slate-100 text-slate-500" : "border-white/20 text-slate-300")} uppercase tracking-[0.15em] text-[10px]`}
    data-testid={`status-badge-${status}`}
  >
    {status}
  </Badge>
);

export const JobForm = ({ initial, onSaved, onCancel, light = false }) => {
  const [form, setForm] = useState(
    initial ? { ...initial, skills: (initial.skills || []).join(", ") } : EMPTY_JOB
  );
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const fieldCls = light ? INPUT_LIGHT : inputCls;
  const labelCls = light ? "text-slate-500" : "text-slate-400";
  const contentCls = light ? SELECT_CONTENT_LIGHT : "bg-surface border-white/10 text-slate-200";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        company: form.company || null,
        experience: form.experience || null,
        salary: form.salary || null,
      };
      if (initial?.id) {
        await api.put(`/jobs/${initial.id}`, payload);
        toast.success("Role updated.");
      } else {
        await api.post("/jobs", payload);
        toast.success("Role posted. It's live on the Careers page.");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className={`rounded-2xl border p-8 space-y-5 ${light ? "border-slate-200 bg-white" : "border-white/10 bg-surface"}`} data-testid="job-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className={labelCls}>Job title</Label>
          <Input required value={form.title} onChange={set("title")} data-testid="jobform-title-input" className={fieldCls} placeholder="Senior React Engineer" />
        </div>
        <div className="space-y-2">
          <Label className={labelCls}>Company (blank = your company)</Label>
          <Input value={form.company || ""} onChange={set("company")} data-testid="jobform-company-input" className={fieldCls} placeholder="Client name" />
        </div>
        <div className="space-y-2">
          <Label className={labelCls}>Location</Label>
          <Input required value={form.location} onChange={set("location")} data-testid="jobform-location-input" className={fieldCls} placeholder="Bengaluru / Remote" />
        </div>
        <div className="space-y-2">
          <Label className={labelCls}>Industry</Label>
          <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
            <SelectTrigger data-testid="jobform-industry-select" className={fieldCls}><SelectValue /></SelectTrigger>
            <SelectContent className={contentCls}>
              {INDUSTRIES.map((i) => <SelectItem key={i} value={i} data-testid={`jobform-industry-${i.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className={labelCls}>Type</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="jobform-type-select" className={fieldCls}><SelectValue /></SelectTrigger>
            <SelectContent className={contentCls}>
              {JOB_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`jobform-type-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className={labelCls}>Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="jobform-status-select" className={fieldCls}><SelectValue /></SelectTrigger>
            <SelectContent className={contentCls}>
              <SelectItem value="open" data-testid="jobform-status-open">Open</SelectItem>
              <SelectItem value="closed" data-testid="jobform-status-closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className={labelCls}>Experience</Label>
          <Input value={form.experience || ""} onChange={set("experience")} data-testid="jobform-experience-input" className={fieldCls} placeholder="3-6 yrs" />
        </div>
        <div className="space-y-2">
          <Label className={labelCls}>Salary range</Label>
          <Input value={form.salary || ""} onChange={set("salary")} data-testid="jobform-salary-input" className={fieldCls} placeholder="18-24 LPA" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className={labelCls}>Skills (comma separated)</Label>
        <Input value={form.skills} onChange={set("skills")} data-testid="jobform-skills-input" className={fieldCls} placeholder="React, TypeScript, GraphQL" />
      </div>
      <div className="space-y-2">
        <Label className={labelCls}>Description</Label>
        <Textarea required rows={5} value={form.description} onChange={set("description")} data-testid="jobform-description-input"
          className={fieldCls} placeholder="What will this person own, build, or lead?" />
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={saving} data-testid="jobform-submit-btn"
          className="rounded-full bg-brand px-7 py-3 text-sm font-medium text-white transition-shadow duration-300 hover:shadow-glow disabled:opacity-50">
          {saving ? "Saving..." : initial?.id ? "Save changes" : "Post role"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} data-testid="jobform-cancel-btn"
            className={`rounded-full border px-7 py-3 text-sm transition-colors duration-300 ${light ? "border-slate-200 text-slate-600 hover:border-slate-400" : "border-white/15 text-slate-300 hover:border-brand/60"}`}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};
